import { useState } from 'react';
import type { ReactNode } from 'react';

import classnames from 'classnames';

import CardStack from '../CardStack';
import SelectedChips from '../SelectedChips';
import ThemeCard from '../ThemeCard';

import type { CardStackWithThemeCardProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/CardStackWithThemeCardProps';
import type {
  Chip,
  ThemeCardProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ThemeCardProps';

import STYLES from './CardStackWithThemeCard.module.scss';

type CardStackWithThemeCardPropsWithExtraProps = CardStackWithThemeCardProps & {
  callToAction?: ReactNode;
};

const CardStackWithThemeCard = ({
  callToAction = null,
  cards,
  disclaimer,
  expandProps = undefined,
  grouping = false,
  imageConfig,
  onCardClick,
  subtitle,
  title,
}: CardStackWithThemeCardPropsWithExtraProps) => {
  const hasCards = !!cards?.length;
  const [activeChipIndex, setActiveChipIndex] = useState(0);
  if (!hasCards) {
    return null;
  }
  const chips = grouping
    ? Array.from(
        new Map(
          cards
            .map((item) => item.groupKey)
            .filter((key): key is Chip => {
              if (key !== undefined) {
                return !!key?.name;
              }

              return false;
            })
            .map((key) => {
              const id = key?.name;
              return [id, key]; // 👈 id as the unique key
            }),
        ).values(),
      )
    : [];
  const activeChip = chips[activeChipIndex];
  const isLargeActiveChip = isLargeChip(activeChip);
  const renderCards = chips?.length
    ? cards.filter((card) => {
        if (card.groupKey === undefined) {
          return false;
        }
        const isActiveGroup = card.groupKey?.name === activeChip?.name;
        return (
          isActiveGroup &&
          (!isLargeActiveChip || (isLargeActiveChip && !isVanCard(card)))
        );
      })
    : cards;
  return (
    <div className={STYLES.CardStackWithThemeCard}>
      <CardStack
        title={title}
        subtitle={subtitle}
        disclaimer={disclaimer}
        callToAction={
          // Hide ExpandableControl if there are less cards than the maxToShowWhenCollapse
          !expandProps || renderCards.length > expandProps.maxToShowWhenCollapse
            ? callToAction
            : null
        }
      >
        <SelectedChips
          chips={chips}
          activeChipIndex={activeChipIndex}
          setActiveChipIndex={setActiveChipIndex}
          cardType="Stack"
        />
        <div className={STYLES.CardStack}>
          {(() => {
            let indexInChip = -1;
            return cards.map((card, index) => {
              const shouldFilterVan =
                grouping && isLargeActiveChip && isVanCard(card);
              const isActiveCard =
                isCurrentChip(grouping, card, activeChip) && !shouldFilterVan;
              indexInChip = isActiveCard ? indexInChip + 1 : indexInChip;
              return (
                <div
                  data-testid={`themeCardGroup${index}`}
                  className={classnames(STYLES.CardStack__card, STYLES.card, {
                    [STYLES.CardStack__hide]:
                      !isActiveCard ||
                      isHiddenByCollapse(
                        grouping ? indexInChip : index,
                        expandProps,
                      ),
                  })}
                >
                  <ThemeCard
                    key={card.title}
                    {...card}
                    index={index}
                    imageConfig={imageConfig}
                    onClick={onCardClick && (() => onCardClick(card))}
                  />
                </div>
              );
            });
          })()}
        </div>
      </CardStack>
    </div>
  );
};

const isCurrentChip = (
  grouping: boolean,
  card: ThemeCardProps,
  activeChip: Chip,
) => !grouping || card.groupKey?.name === activeChip?.name;

const isLargeChip = (chip?: Chip) =>
  chip?.name?.toLowerCase().includes('large') ?? false;

const isVanCard = (card: ThemeCardProps) => {
  // Large is a car class; van / people carrier / SUV are subclasses.
  // As per product (https://skyscanner.slack.com/archives/C0A3FR8EAHK/p1765777367808509), vans won't coexist with Large
  // except for rare fullsize/luxury vans, so filtering vans when Large is active is safe.
  const subClass = card?.trackingData?.subClass?.toLowerCase() ?? '';
  const groupName = card?.groupKey?.name?.toLowerCase() ?? '';
  return groupName.includes('van') || subClass.includes('van');
};

const isHiddenByCollapse = (
  index: number,
  expandProps?: {
    maxToShowWhenCollapse: number;
    isExpand: boolean;
  },
) =>
  expandProps?.isExpand === false && index >= expandProps.maxToShowWhenCollapse;

export default CardStackWithThemeCard;
